<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { AccountClient, ApiError, dateTime } from '../api';
import Icon from './Icon.vue';
import Dialog from './Dialog.vue';
import ConnectorHistory from './ConnectorHistory.vue';

type State = { items: { id: string; url: string }[]; recent: { type: string; at: number; success: boolean; detail: string }[] };
const props = defineProps<{ client: AccountClient }>();
const emit = defineEmits<{ notice: [message: string, kind?: 'success' | 'error' | 'loading' | 'info']; reauth: [retry: () => Promise<void>] }>();
const path = '/api/admin/connectors/ha-status';
const state = ref<State | null>(null), loading = ref(false), busy = ref(false), error = ref('');
const id = ref(''), url = ref(''), removing = ref('');
async function load() {
  loading.value = true; error.value = '';
  try { state.value = await props.client.request<State>(path); } catch (err: any) { error.value = err.message; }
  finally { loading.value = false; }
}
async function mutate(method: string, target: string, data?: { id: string; url: string }) {
  if (busy.value) return;
  busy.value = true; error.value = '';
  try {
    state.value = await props.client.request<State>(target, method, data);
    if (method === 'POST') { id.value = ''; url.value = ''; }
    removing.value = ''; emit('notice', method === 'POST' ? 'type 已加入白名单' : 'type 已从白名单删除', 'success');
  } catch (err: any) {
    if (err instanceof ApiError && err.status === 428) { removing.value = ''; emit('reauth', () => mutate(method, target, data)); }
    else { error.value = err.message; emit('notice', err.message, 'error'); }
  } finally { busy.value = false; }
}
onMounted(load);
</script>

<template>
  <section class="ha-connector">
    <div class="section-heading"><h2>HA Status Webhook</h2><button class="text-button" :disabled="busy || loading" @click="load">刷新</button></div>
    <p class="muted">按 type 白名单，将消息转发给 Home Assistant。</p>
    <p v-if="error" class="inline-error" role="alert">{{ error }}</p>
    <div v-if="loading && !state" class="empty-block"><Icon name="LoaderCircle" class="spin" />正在读取白名单</div>
    <template v-else-if="state">
      <section class="settings-group">
        <div v-if="!state.items.length" class="empty-block">还没有 type。添加后即可接收对应消息。</div>
        <div v-for="item in state.items" :key="item.id" class="settings-row"><span class="icon-tile"><Icon name="House" /></span><div class="grow ha-target"><strong>{{ item.id }}</strong><p>{{ item.url }}</p></div><button class="text-button danger-text" :disabled="busy" @click="removing = item.id">删除</button></div>
        <form class="ha-form" @submit.prevent="mutate('POST', path, { id, url })">
          <fieldset :disabled="busy"><h3>添加 type</h3><label><span>type ID</span><input v-model="id" required maxlength="64" pattern="[A-Za-z0-9_-]{1,64}" placeholder="例如：doorbell" autocomplete="off" /></label><label><span>内网 HA Webhook URL</span><input v-model="url" required type="url" maxlength="2048" placeholder="http://192.168.1.2:8123/api/webhook/你的ID" autocomplete="off" /></label><p class="footnote">ID 区分大小写；URL 支持内网或 Tailscale IP，加密保存，仅用于后端转发。</p><button class="primary"><Icon v-if="busy" name="LoaderCircle" class="spin" />{{ busy ? '正在保存' : '添加到白名单' }}</button></fieldset>
        </form>
      </section>
      <section class="settings-group ha-form"><h3>调用方式</h3><code>POST /api/webhook/hastatus</code><pre>{ "type": "doorbell", "message": "有人按门铃" }</pre><p class="footnote">使用 application/json，无需登录或密钥，知道有效 type 的调用者均可发送。命中后仅向 HA 发送 { "message": "有人按门铃" }，不转发 type 或其他字段。</p></section>
      <ConnectorHistory title="最近转发" :count="state.recent.length" :latest="state.recent[0]?.detail"><section class="settings-group"><p v-if="!state.recent.length" class="empty-block">暂无转发记录</p><div v-for="(item, index) in state.recent" :key="`${item.at}-${index}`" class="settings-row"><Icon :name="item.success ? 'CircleCheck' : 'CircleX'" :class="{ 'danger-text': !item.success }" /><div class="grow"><strong>{{ item.type }} · {{ item.detail }}</strong><p>{{ dateTime(item.at) }}</p></div></div></section><p class="footnote">保留最近 20 次转发结果，不保存 message。成功仅表示 HA Webhook 已接收。</p></ConnectorHistory>
    </template>
    <Dialog :open="Boolean(removing)" title="删除 type" :busy="busy" @close="removing = ''"><p>删除 {{ removing }} 后，该 type 的新请求将不再转发。</p><footer class="dialog-actions"><button class="secondary" :disabled="busy" @click="removing = ''">取消</button><button class="primary" :disabled="busy" @click="mutate('DELETE', `${path}/${encodeURIComponent(removing)}`)">确认删除</button></footer></Dialog>
  </section>
</template>

<style scoped>
.ha-connector { margin-top: 40px; }
.ha-form { padding: 24px; margin-top: 20px; }
fieldset { border: 0; padding: 0; min-width: 0; }
.ha-target { min-width: 0; }
.ha-target p, code, pre { overflow-wrap: anywhere; white-space: pre-wrap; }
@media (max-width: 600px) { .ha-form { padding: 18px; } .settings-row { flex-wrap: wrap; } }
</style>
